import { useImmerAtom } from 'jotai-immer';
import { splitOrderInvoicesSearchFormAtom } from './split_order_invoices_search_atoms';
import { SplitOrderInvoicesSearchModel } from './split_order_invoices_search_types';
import { useCallback } from 'react';
import { ngContext, ngDelegate } from '../../../../core/services/ng.delegate';
import { useSplitOrderInvoicesStoreActions } from '../pagehooks/useSplitOrderInvoicesStore';
import { SPLIT_ORDER_INVOICES_LIST_FORM_KEY } from '../../split_order_invoices_const';
import tableViewRepo from '../../../../core/repo/viewStateRepo';
import { stripAwayFormErrors } from '../../../../core/table/utils/stripAwayFormErrors';
import utilSvc from '../../../../core/utilSvc';
import { ValidOption } from '../../../../core/components/validation/types';

type UseSplitOrderInvoicesSearchStateReturn = {
    state: SplitOrderInvoicesSearchModel;
    setProperty: (key: keyof SplitOrderInvoicesSearchModel, val: any) => void;
    setMultipleProperties: (updates: Partial<Record<keyof SplitOrderInvoicesSearchModel, any>>) => void;
    handleRxChange: (val: string) => void;
    handleCeChange: (val: string) => void;
    toggleRebatesOnly: (checked: boolean) => void;
    handleSearch: () => void;
};

export const useSplitOrderInvoicesSearchState = (): UseSplitOrderInvoicesSearchStateReturn => {
    const [state, setState] = useImmerAtom(splitOrderInvoicesSearchFormAtom);
    const { getList } = useSplitOrderInvoicesStoreActions();

    const setProperty = useCallback(
        (key: keyof SplitOrderInvoicesSearchModel, val: any): void => {
            setState(draft => {
                (draft[key] as any).val = val;
                if (key === 'RX' || key === 'CE') {
                    (draft[key] as any).err = '';
                }
            });
        },
        [setState],
    );

    // RX change — cascade: rebuild CE options from affiliations filtered by selected RXID
    const handleRxChange = useCallback(
        (val: string): void => {
            const ctx = ngContext();
            let affs = utilSvc.aff.findStatus(ctx.User?.AffList ?? []);
            affs = utilSvc.aff.findRxType(affs, ['DISPENSING', 'CENTRALFILL']);
            affs = utilSvc.aff.findRxModel(affs, utilSvc.aff.rxModelsByFeature('invoice.split'));
            // ceDroplist filtered by rxid returns {label, value} DroplistItems
            const ceDroplist = val ? (utilSvc.aff.ceDroplist(affs, val, false, true) ?? []) : [];
            const ceOptions: ValidOption[] = ceDroplist.map((ce: any) => ({
                lbl: ce.value === 'ALL' ? 'All Facilities' : ce.label,
                val: ce.value,
            }));
            const defaultCe = 'ALL';
            setState(draft => {
                draft.RX.val = val;
                draft.RX.err = val ? '' : 'Required';
                draft.CE.val = defaultCe;
                draft.CE.options = ceOptions;
                draft.CE.err = '';
            });
        },
        [setState],
    );

    const handleCeChange = useCallback(
        (val: string): void => {
            setState(draft => {
                draft.CE.val = val;
                draft.CE.err = val ? '' : 'Required';
            });
        },
        [setState],
    );

    const toggleRebatesOnly = useCallback(
        (checked: boolean): void => {
            setState(draft => {
                draft.RebatesOnly = checked;
            });
        },
        [setState],
    );

    const setMultipleProperties = useCallback(
        (updates: Partial<Record<keyof SplitOrderInvoicesSearchModel, any>>): void => {
            setState(draft => {
                for (const [key, val] of Object.entries(updates)) {
                    const k = key as keyof SplitOrderInvoicesSearchModel;
                    if (k === 'RebatesOnly') {
                        (draft as any).RebatesOnly = val;
                    } else {
                        (draft[k] as any).val = val;
                    }
                }
            });
        },
        [setState],
    );

    const validate = useCallback((): boolean => {
        let valid = true;
        setState(draft => {
            if (!draft.RX.val || draft.RX.val === draft.RX.def || draft.RX.val === 'NONE') {
                draft.RX.err = 'Pharmacy is required.';
                valid = false;
            }
            if (!draft.StartDate.val || !draft.EndDate.val) {
                valid = false;
            }
        });
        return valid;
    }, [setState]);

    const handleSearch = useCallback((): void => {
        if (!validate()) {
            ngDelegate.ok('Cannot Search', 'Check search properties');
            return;
        }
        tableViewRepo.setForm(SPLIT_ORDER_INVOICES_LIST_FORM_KEY, stripAwayFormErrors(state));
        getList(state);
    }, [validate, state, getList]);

    return {
        state,
        setProperty,
        setMultipleProperties,
        handleRxChange,
        handleCeChange,
        toggleRebatesOnly,
        handleSearch,
    };
};
